import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useMultipleFormsValidation from '../../../../../hooks/useMultipleFormsValidation';
import useWorkerSubstanceFieldsConfig from './useWorkerSubstanceFieldsConfig';

interface SubstanceFormData {
    expositionDateStart: string;
    expositionDateEnd: string;
    medicalExams: any[];
    substanceId: string | number;
    substanceCode: string;
    substanceDescription: string;
}

const useWorkerSubstancesForm = (
    initialDataArray: SubstanceFormData[] = [],
    ingressDate: string | ''
) => {
    const [formsData, setFormsData] = useState<SubstanceFormData[]>(initialDataArray);

    // Update formsData when initialDataArray changes
    useEffect(() => {
        if (initialDataArray && initialDataArray.length > 0) {
            setFormsData(initialDataArray);
        }
    }, [initialDataArray]);

    const { t } = useTranslation('carcinogenicReport', {
        keyPrefix: 'modules.workersAndMedicalExams'
    });

    const {
        errors,
        hasValidationErrors,
        validateFormAtIndex,
        handleBlurValidation,
        clearFieldError,
        resetValidation,
        setErrors
    } = useMultipleFormsValidation();

    // Get the base field config
    const { workerSubstanceFieldsConfig } = useWorkerSubstanceFieldsConfig({
        t,
        wokerFormData: formsData[0] || {},
        handleBlurValidation: (name: string, hasError: boolean, errorText: string) => {
            // This won't be used directly
        },
        ingressDate: ingressDate
    });

    // Create a wrapper for blur validation that includes the form index
    const handleFieldBlur = (
        formIndex: number,
        name: string,
        hasError: boolean,
        errorText: string
    ) => {
        handleBlurValidation(formIndex, name, hasError, errorText);
    };

    const formConfigs = useMemo(() => {
        return formsData.map((formData) => {
            // Return the base config - blur handling will be done separately
            return workerSubstanceFieldsConfig;
        });
    }, [workerSubstanceFieldsConfig, formsData]);

    const handleChange = (index: number, name: string, value: any) => {
        setFormsData((prevState) => {
            const newState = [...prevState];
            newState[index] = {
                ...newState[index],
                [name]: value
            };
            return newState;
        });

        clearFieldError(index, name);
    };

    const validateAllForms = () => {
        let isValid = true;
        formsData.forEach((formData, index) => {
            if (!validateFormAtIndex(index, formData, workerSubstanceFieldsConfig)) {
                isValid = false;
            }
        });
        return isValid;
    };
    const handleSubmitSubstancesAndMedicalExaminations = () => {
        return validateAllForms();
    };

    return {
        formsData,
        errors,
        hasValidationErrors,
        handleChange,
        handleSubmitSubstancesAndMedicalExaminations,
        formConfigs,
        resetValidation,
        handleFieldBlur
    };
};

export default useWorkerSubstancesForm;
